import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';
import { useUnreadCount } from '../../lib/hooks';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  FolderOpen, 
  CreditCard,
  Handshake, 
  LogOut, 
  Menu,
  User,
  MessageSquare,
  BarChart3,
  Settings,
  Calendar,
  BookMarked,
  Mail,
  UserPlus,
  Plane,
  Database,
  TrendingUp
} from 'lucide-react';
import { useState } from 'react';

const navItems = {
  admin: [
    { to: '/dashboard/admin', icon: LayoutDashboard, tKey: 'sidebar_dashboard' },
    { to: '/admin/users', icon: Users, tKey: 'sidebar_users' },
    { to: '/students', icon: Users, tKey: 'sidebar_students' },
    { to: '/courses', icon: BookOpen, tKey: 'sidebar_courses' },
    { to: '/bookings', icon: Calendar, tKey: 'sidebar_calendar' },
    { to: '/dossiers', icon: FolderOpen, tKey: 'sidebar_dossiers' },
    { to: '/arrivals', icon: Plane, tKey: 'sidebar_arrivals' },
    { to: '/payments', icon: CreditCard, tKey: 'sidebar_payments' },
    { to: '/partners', icon: Handshake, tKey: 'sidebar_partners' },
    { to: '/resources-admin', icon: BookMarked, tKey: 'sidebar_resources' },
    { to: '/contact-messages', icon: Mail, tKey: 'sidebar_contact' },
    { to: '/leads', icon: UserPlus, tKey: 'sidebar_leads' },
    { to: '/messages', icon: MessageSquare, tKey: 'sidebar_messages' },
    { to: '/analytics/teachers', icon: TrendingUp, tKey: 'sidebar_analytics' },
    { to: '/statistics', icon: BarChart3, tKey: 'sidebar_stats' },
    { to: '/settings', icon: Settings, tKey: 'sidebar_settings' },
  ],
  resp_peda: [
    { to: '/dashboard/admin', icon: LayoutDashboard, tKey: 'sidebar_dashboard' },
    { to: '/students', icon: Users, tKey: 'sidebar_students' },
    { to: '/courses', icon: BookOpen, tKey: 'sidebar_courses' },
    { to: '/analytics/teachers', icon: TrendingUp, tKey: 'sidebar_analytics' },
    { to: '/messages', icon: MessageSquare, tKey: 'sidebar_messages' },
    { to: '/settings', icon: Settings, tKey: 'sidebar_settings' },
  ],
  resp_admin: [
    { to: '/dashboard/admin', icon: LayoutDashboard, tKey: 'sidebar_dashboard' },
    { to: '/students', icon: Users, tKey: 'sidebar_students' },
    { to: '/bookings', icon: Calendar, tKey: 'sidebar_calendar' },
    { to: '/dossiers', icon: FolderOpen, tKey: 'sidebar_dossiers' },
    { to: '/arrivals', icon: Plane, tKey: 'sidebar_arrivals' },
    { to: '/payments', icon: CreditCard, tKey: 'sidebar_payments' },
    { to: '/partners', icon: Handshake, tKey: 'sidebar_partners' },
    { to: '/messages', icon: MessageSquare, tKey: 'sidebar_messages' },
    { to: '/settings', icon: Settings, tKey: 'sidebar_settings' },
  ],
  teacher: [
    { to: '/dashboard/teacher', icon: LayoutDashboard, tKey: 'sidebar_dashboard' },
    { to: '/students', icon: Users, tKey: 'sidebar_students' },
    { to: '/courses', icon: BookOpen, tKey: 'sidebar_courses' },
    { to: '/messages', icon: MessageSquare, tKey: 'sidebar_messages' },
  ],
  student: [
    { to: '/dashboard/student', icon: LayoutDashboard, tKey: 'sidebar_my_dashboard' },
    { to: '/student/courses', icon: BookOpen, tKey: 'sidebar_my_courses' },
    { to: '/student/dossier', icon: FolderOpen, tKey: 'sidebar_my_dossier' },
    { to: '/my-payments', icon: CreditCard, tKey: 'sidebar_my_payments' },
    { to: '/messages', icon: MessageSquare, tKey: 'sidebar_messages' },
    { to: '/appointments', icon: Calendar, tKey: 'sidebar_appointments' },
    { to: '/resources', icon: BookMarked, tKey: 'sidebar_resources' },
  ],
  partner: [
    { to: '/dashboard/partner', icon: LayoutDashboard, tKey: 'sidebar_dashboard' },
    { to: '/my-candidates', icon: Users, tKey: 'sidebar_my_candidates' },
    { to: '/arrivals', icon: Plane, tKey: 'sidebar_arrivals' },
    { to: '/messages', icon: MessageSquare, tKey: 'sidebar_messages' },
  ],
};

const SidebarContent = ({ onClose }) => {
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count || 0;
  const items = navItems[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (onClose) onClose();
  };

  return (
    <div className="flex flex-col h-full bg-secondary text-white">
      {/* Logo */}
      <div className="bg-white flex items-center justify-center px-6 py-3 border-b border-white/10">
        <img
          src="https://customer-assets.emergentagent.com/job_ec0e6c28-2dec-4a8b-9e46-60d9bf9b919f/artifacts/brhzx1m4_Logo%20no%20background.png"
          alt="Speak&Move Global Institute"
          className="w-full h-auto object-contain"
          style={{ marginTop: '-52px', marginBottom: '-52px' }}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {items.map((item) => {
          const isMessaging = item.to === '/messages';
          
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white text-secondary font-medium shadow-sm'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`
              }
              data-testid={`nav-${item.to.replace(/\//g, '-').slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{t(item.tKey, lang)}</span>
              {isMessaging && unreadCount > 0 && (
                <Badge className="bg-accent text-white text-xs h-5 min-w-[20px] flex items-center justify-center" data-testid="unread-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 mb-3">
          <Avatar className="w-9 h-9 border-2 border-white/20">
            <AvatarImage src={user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : undefined} />
            <AvatarFallback className="bg-primary text-white text-sm font-medium">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-white/60 capitalize">
              {t(`role_${user?.role}`, lang)}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-3" />
          {t('sidebar_logout', lang)}
        </Button>
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Mobile trigger */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="fixed top-4 left-4 z-50 md:hidden bg-white shadow-md"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-64">
          <SidebarContent onClose={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 h-screen fixed left-0 top-0 z-40">
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
